import { useState } from 'react';
import {
  Monitor, Tablet, Smartphone,
  Eye, Save, History, ChevronLeft, Sparkles, X, FileJson, FileCode, Sun, Moon, CheckCircle2,
} from 'lucide-react';
import { cn } from '../utils';
import { Button, Tooltip, Modal } from '../ui';
import { useEditorStore, useVersionsStore } from '../store';
import { exportHtml } from '../services/export';
import { formatDate, downloadBlob } from '../utils';
import { useTheme } from '../../../../contexts/ThemeContext';

function PreviewToggle() {
  const { previewMode, setPreviewMode } = useEditorStore();
  const modes = [
    { id: 'desktop' as const, icon: Monitor },
    { id: 'tablet' as const, icon: Tablet },
    { id: 'mobile' as const, icon: Smartphone },
  ];

  return (
    <div className="flex items-center gap-0.5 bg-gray-100 dark:bg-dark-800 rounded-xl p-0.5 border border-line/60 dark:border-dark-700">
      {modes.map(({ id, icon: Icon }) => (
        <Tooltip key={id} content={id.charAt(0).toUpperCase() + id.slice(1)}>
          <button
            onClick={() => setPreviewMode(id)}
            className={cn(
              'p-1.5 rounded-lg transition-all duration-150',
              previewMode === id
                ? 'bg-violet-600 text-white shadow-sm shadow-violet-500/20'
                : 'text-content-subtle dark:text-dark-500 hover:text-gray-700 dark:hover:text-white hover:bg-surface dark:hover:bg-dark-700'
            )}
          >
            <Icon className="size-3.5" />
          </button>
        </Tooltip>
      ))}
    </div>
  );
}

function HistoryModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { design, loadDesign } = useEditorStore();
  const { getVersions } = useVersionsStore();
  const versions = getVersions(design.id);

  return (
    <Modal open={open} onClose={onClose} title="Version History" size="md">
      {versions.length === 0 ? (
        /* ── Premium empty state ── */
        <div className="flex flex-col items-center gap-3 py-10 text-center">
          <div className="w-12 h-12 rounded-2xl bg-gray-100 dark:bg-dark-700
            flex items-center justify-center">
            <History className="size-5 text-content-subtle dark:text-dark-500" />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-700 dark:text-dark-200">
              No saved versions yet
            </p>
            <p className="text-xs text-content-subtle dark:text-dark-500 mt-1 max-w-[220px] mx-auto leading-relaxed">
              Hit <kbd className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-dark-700
                text-[10px] font-mono text-content-secondary dark:text-dark-300 border
                border-line dark:border-dark-600">Save</kbd> to create
              a snapshot you can restore later.
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-1.5">
          {versions.map((v, i) => (
            <div
              key={v.id}
              className="group flex items-center gap-3 p-3 rounded-xl
                border border-transparent
                hover:border-line-subtle dark:hover:border-dark-600
                hover:bg-gray-50 dark:hover:bg-dark-700/60
                transition-all duration-150"
            >
              {/* Timeline dot */}
              <div className="relative flex flex-col items-center shrink-0">
                <div className={cn(
                  'w-2 h-2 rounded-full',
                  i === 0 ? 'bg-violet-500' : 'bg-gray-300 dark:bg-dark-600'
                )} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-800 dark:text-dark-100 truncate">
                  {v.label}
                </p>
                <p className="text-xs text-content-subtle dark:text-dark-500 mt-0.5">
                  {formatDate(v.createdAt)}
                </p>
              </div>
              <Button
                size="xs"
                variant="outline"
                onClick={() => { loadDesign(v.snapshot); onClose(); }}
              >
                Restore
              </Button>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

function PreviewModal({ open, onClose, templateName }: { open: boolean; onClose: () => void; templateName: string }) {
  const { design, previewMode } = useEditorStore();
  const html = exportHtml(design);
  const widths = { desktop: '100%', tablet: '768px', mobile: '375px' };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex flex-col bg-dark-900">
      {/* Header */}
      <div className="flex items-center justify-between px-4 h-12 border-b border-dark-700 shrink-0">
        <div className="flex items-center gap-3">
          <button aria-label="Close" onClick={onClose} className="text-dark-400 hover:text-white transition-colors">
            <X className="size-4" />
          </button>
          <span className="text-sm font-medium text-white">{templateName} — Preview</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-dark-400">
          <Monitor className="size-3.5" />
          <span className="capitalize">{previewMode}</span>
        </div>
      </div>
      {/* Preview */}
      <div className="flex-1 overflow-auto bg-dark-800 p-8 flex justify-center">
        <div
          style={{ width: widths[previewMode], maxWidth: '100%' }}
          className="transition-all duration-300 bg-surface rounded-xl shadow-2xl overflow-hidden"
        >
          <iframe
            title="Design Preview"
            srcDoc={html}
            className="w-full border-none"
            style={{ height: '80vh', minHeight: 500 }}
          />
        </div>
      </div>
    </div>
  );
}

interface EditorToolbarProps {
  templateName: string;
  onBack?: () => void;
  onSave?: () => void | Promise<void>;
  onExportHtml?: () => void;
  onExportJson?: () => void;
}

export function EditorToolbar({ templateName, onBack, onSave, onExportHtml, onExportJson }: EditorToolbarProps) {
  const { design, isDirty, isSaving, setIsSaving, markClean } = useEditorStore();
  const { addVersion } = useVersionsStore();
  const { isDark, toggleTheme } = useTheme();
  const [historyOpen, setHistoryOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [savedRecently, setSavedRecently] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      addVersion(design.id, design);
      if (onSave) await onSave();
      markClean();
      setSavedRecently(true);
      setTimeout(() => setSavedRecently(false), 1500);
    } catch {
      // onSave threw — keep isDirty true so user can retry
    } finally {
      setIsSaving(false);
    }
  };

  const handleExport = (format: 'html' | 'json') => {
    if (format === 'html') {
      if (onExportHtml) { onExportHtml(); return; }
      const html = exportHtml(design);
      downloadBlob(html, `${templateName.toLowerCase().replace(/\s+/g, '-')}.html`);
    } else {
      if (onExportJson) { onExportJson(); return; }
      const json = JSON.stringify(design, null, 2);
      downloadBlob(json, `${templateName.toLowerCase().replace(/\s+/g, '-')}.json`, 'application/json');
    }
  };

  return (
    <>
      <header className="flex items-center gap-2 px-3 h-12 shrink-0 z-30
        border-b border-line/60 dark:border-dark-700
        bg-white/95 dark:bg-dark-900/95 backdrop-blur-md
        shadow-sm shadow-gray-100/80 dark:shadow-black/20">
        {/* Left — shrink-0 so it never collapses */}
        <div className="flex items-center gap-2 shrink-0">
          <Tooltip content="Back to Projects" side="bottom">
            <Button variant="ghost" size="xs" icon={<ChevronLeft className="size-3.5" />} onClick={onBack} />
          </Tooltip>
          <div className="h-4 w-px bg-gray-200 dark:bg-dark-600" />
          <div className="flex items-center gap-1.5">
            <span
              title={templateName}
              className="text-sm font-bold text-content dark:text-white truncate max-w-[180px]"
            >
              {templateName}
            </span>
            {isDirty && !savedRecently && (
              <span className="text-xs text-amber-500 dark:text-amber-400 font-semibold shrink-0">• Unsaved</span>
            )}
            {savedRecently && (
              <span className="text-xs text-emerald-500 dark:text-emerald-400 font-semibold shrink-0 flex items-center gap-0.5">
                <CheckCircle2 className="size-3" /> Saved
              </span>
            )}
          </div>
        </div>

        {/* Center — takes remaining space, preview toggle centered */}
        <div className="flex-1 flex justify-center">
          <PreviewToggle />
        </div>

        {/* Right — shrink-0 so all buttons always visible */}
        <div className="flex items-center gap-0.5 shrink-0">
          {/* Theme toggle */}
          <Tooltip content={isDark ? 'Light Mode' : 'Dark Mode'} side="bottom">
            <button aria-label="Toggle theme"
              onClick={toggleTheme}
              className="w-7 h-7 flex items-center justify-center rounded-lg
                text-content-muted dark:text-dark-400
                hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors"
            >
              {isDark ? <Sun className="size-3.5" /> : <Moon className="size-3.5" />}
            </button>
          </Tooltip>
          <Tooltip content="Version History" side="bottom">
            <Button variant="ghost" size="xs" icon={<History className="size-3.5" />} onClick={() => setHistoryOpen(true)} />
          </Tooltip>
          <Tooltip content="AI Assistant (coming soon)" side="bottom">
            <Button variant="ghost" size="xs" icon={<Sparkles className="size-3.5 text-accent" />} />
          </Tooltip>
          <Tooltip content="Preview" side="bottom">
            <Button variant="ghost" size="xs" icon={<Eye className="size-3.5" />} onClick={() => setPreviewOpen(true)} />
          </Tooltip>
          <Tooltip content="Export HTML" side="bottom">
            <Button variant="ghost" size="xs" icon={<FileCode className="size-3.5" />} onClick={() => handleExport('html')} />
          </Tooltip>
          <Tooltip content="Export JSON" side="bottom">
            <Button variant="ghost" size="xs" icon={<FileJson className="size-3.5" />} onClick={() => handleExport('json')} />
          </Tooltip>
          <div className="w-px h-4 bg-gray-200 dark:bg-dark-600 mx-0.5" />
          <Button
            variant={savedRecently ? 'success' : 'primary'}
            size="sm"
            icon={savedRecently
              ? <CheckCircle2 className="size-3.5" />
              : <Save className="size-3.5" />}
            loading={isSaving}
            onClick={handleSave}
            disabled={!isDirty && !isSaving}
          >
            {isSaving ? 'Saving…' : savedRecently ? 'Saved!' : 'Save'}
          </Button>
        </div>

        <HistoryModal open={historyOpen} onClose={() => setHistoryOpen(false)} />
      </header>

      <PreviewModal open={previewOpen} onClose={() => setPreviewOpen(false)} templateName={templateName} />
    </>
  );
}
